/**
 * La mesure d'audience du back-office (PostHog) — décision du porteur du produit
 * du 2026-10-29.
 *
 * **Un écran d'opérateur est plein de données de clients**, et c'est ce qui dicte
 * tout ce fichier :
 *
 * - **aucune capture automatique** : `posthog-js` relève par défaut le texte de
 *   l'élément cliqué — ici, un nom, un numéro, un montant ;
 * - **aucun enregistrement de session**, ni carte de chaleur, ni clic mort ;
 * - **chaque adresse est nettoyée avant de partir** : un segment qui porte un
 *   chiffre devient `:id`, la requête et l'ancre disparaissent. `/clients/42?q=77…`
 *   part comme `/clients/:id`. PostHog joint l'adresse courante à CHAQUE
 *   événement, pas seulement aux pages vues : le nettoyage porte donc sur tous.
 *
 * L'opérateur n'est désigné que par son identifiant **opaque**, rendu par le
 * serveur avec la session — jamais par son nom ni son identifiant de connexion.
 *
 * **La clé n'est pas compilée.** L'image du back-office est la même en
 * préproduction et en production (voir le `Dockerfile`) : la clé est lue au
 * démarrage dans `/analytics-config.json`, que le conteneur écrit depuis son
 * environnement. Absent ou vide, la mesure est éteinte.
 */

import type { PostHog } from 'posthog-js';

/** Ce que l'application sait dire à la mesure d'audience. Rien ne lève. */
export interface Analytics {
  /** Rattache ce qui suit à l'identifiant opaque de l'opérateur. */
  identify(operatorId: string): void;
  /** Oublie l'opérateur — un poste de back-office est souvent partagé. */
  reset(): void;
  /** Une page vue. Le chemin est nettoyé ici, quel que soit l'appelant. */
  page(pathname: string): void;
}

/** La mesure éteinte : rien ne part. */
export const noAnalytics: Analytics = {
  identify: () => undefined,
  reset: () => undefined,
  page: () => undefined,
};

/** Ce que le conteneur écrit au démarrage. */
export interface AnalyticsConfig {
  readonly posthogKey?: string;
  readonly posthogHost?: string;
}

/** L'adresse de la configuration, servie par le même nginx que l'application. */
export const analyticsConfigPath = '/analytics-config.json';

const instanceParDefaut = 'https://us.i.posthog.com';

/**
 * Le chemin sans ce qui désigne quelqu'un : tout segment qui porte un chiffre —
 * un identifiant, une référence, un numéro — devient `:id`.
 */
export function sanitizePath(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => (/\d/.test(segment) ? ':id' : segment))
    .join('/');
}

/** L'adresse réduite à son origine et à son chemin nettoyé. */
export function sanitizeUrl(adresse: string): string {
  let lue: URL;
  try {
    lue = new URL(adresse);
  } catch {
    // `$direct`, une chaîne vide : rien d'une adresse, rien à nettoyer.
    return adresse;
  }
  return lue.origin + sanitizePath(lue.pathname);
}

/** Les propriétés que `posthog-js` remplit d'une adresse. */
const proprietesDAdresse = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_referrer',
] as const;

/** Celles qu'il remplit d'un chemin. */
const proprietesDeChemin = [
  '$pathname',
  '$initial_pathname',
  '$prev_pageview_pathname',
  '$session_entry_pathname',
] as const;

type Proprietes = Record<string, unknown>;

function nettoyer(proprietes: Proprietes | undefined): void {
  if (proprietes === undefined) return;
  for (const nom of proprietesDAdresse) {
    const valeur = proprietes[nom];
    if (typeof valeur === 'string') proprietes[nom] = sanitizeUrl(valeur);
  }
  for (const nom of proprietesDeChemin) {
    const valeur = proprietes[nom];
    if (typeof valeur === 'string') proprietes[nom] = sanitizePath(valeur);
  }
  // Le titre d'onglet peut nommer la fiche ouverte : il ne part pas.
  delete proprietes.$title;
}

/** Un événement tel que `before_send` le reçoit. */
export interface ScrubbableEvent {
  properties: Proprietes;
  $set?: Proprietes;
  $set_once?: Proprietes;
}

/** Le dernier filtre avant l'envoi, sur TOUT événement. */
export function scrubEvent<T extends ScrubbableEvent>(evenement: T): T {
  nettoyer(evenement.properties);
  nettoyer(evenement.$set);
  nettoyer(evenement.$set_once);
  return evenement;
}

/** La mesure branchée sur une instance de `posthog-js` déjà initialisée. */
export function postHogAnalytics(
  posthog: Pick<PostHog, 'identify' | 'reset' | 'capture'>,
  origine: string = globalThis.location.origin,
): Analytics {
  return {
    identify: (operatorId) => {
      posthog.identify(operatorId);
    },
    reset: () => {
      posthog.reset();
    },
    page: (pathname) => {
      posthog.capture('$pageview', {
        $current_url: origine + sanitizePath(pathname),
        $pathname: sanitizePath(pathname),
      });
    },
  };
}

/**
 * La mesure, utilisable AVANT d'être démarrée : les appels attendent, puis
 * partent dans l'ordre.
 *
 * C'est ce qui permet de monter l'application sans attendre la lecture de la
 * configuration — et sans perdre la première page vue, qui précède toujours la
 * réponse.
 */
export function deferredAnalytics(demarrage: Promise<Analytics>): Analytics {
  let prete: Analytics | null = null;
  const enAttente: ((mesure: Analytics) => void)[] = [];
  void demarrage.then((mesure) => {
    prete = mesure;
    for (const appel of enAttente.splice(0)) appel(mesure);
  });
  const passer = (appel: (mesure: Analytics) => void): void => {
    if (prete === null) enAttente.push(appel);
    else appel(prete);
  };
  return {
    identify: (operatorId) => passer((mesure) => mesure.identify(operatorId)),
    reset: () => passer((mesure) => mesure.reset()),
    page: (pathname) => passer((mesure) => mesure.page(pathname)),
  };
}

/**
 * Lit la configuration et démarre la mesure si une clé est servie.
 *
 * **Ne lève jamais** : une mesure d'audience indisponible n'est pas une raison de
 * refuser un écran à un opérateur.
 */
export async function startAnalytics(
  lire: (adresse: string) => Promise<Response> = (adresse) =>
    fetch(adresse, { cache: 'no-store' }),
): Promise<Analytics> {
  let config: AnalyticsConfig;
  try {
    const reponse = await lire(analyticsConfigPath);
    if (!reponse.ok) return noAnalytics;
    config = (await reponse.json()) as AnalyticsConfig;
  } catch {
    return noAnalytics;
  }
  const cle = config.posthogKey?.trim() ?? '';
  if (cle === '') return noAnalytics;

  try {
    // Chargée à la demande : sans clé, la bibliothèque n'est jamais téléchargée.
    const { default: posthog } = await import('posthog-js');
    posthog.init(cle, {
      api_host: config.posthogHost?.trim() || instanceParDefaut,
      // Les pages sont rapportées par `AnalyticsPageViews`, nettoyées.
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      rageclick: false,
      capture_dead_clicks: false,
      enable_heatmaps: false,
      capture_exceptions: false,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_web_experiments: true,
      advanced_disable_flags: true,
      // L'onglet, comme la session de l'opérateur : rien ne survit au poste
      // refermé.
      persistence: 'sessionStorage',
      person_profiles: 'identified_only',
      before_send: (evenement) => (evenement === null ? null : scrubEvent(evenement)),
    });
    return postHogAnalytics(posthog);
  } catch {
    return noAnalytics;
  }
}
